import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  WEBSITE_PACKAGES,
  WEBSITE_EXPRESS,
  WEBSITE_BETRIEB,
  WEBSITE_SUPPORT,
} from "@/config/content";

// Frontend-Fallbacks — identisch mit backend/ai_manager.py DEFAULT_CONTENT.
// Werden nur genutzt, wenn der Content-Endpunkt nicht erreichbar ist.
export const DEFAULT_CONTENT = {
  prices: {
    website_plus: WEBSITE_PACKAGES.find((p) => p.id === "plus").price,
    website_premium: WEBSITE_PACKAGES.find((p) => p.id === "premium").price,
    website_express: WEBSITE_EXPRESS.price,
    website_betrieb: WEBSITE_BETRIEB.price,
    support_basic: WEBSITE_SUPPORT.find((s) => s.id === "basic").price,
    support_premium: WEBSITE_SUPPORT.find((s) => s.id === "premium").price,
    design_standard: 10000,
    design_premium: 13000,
    brand_starter: 19900,
    brand_complete: 59900,
  },
  colors: { accent: "#d4af37", accent_dark: "#a8842c" },
  sections: {
    brand_statement: true,
    webdesign: true,
    clothing_design: true,
    clothing_brand: true,
    portfolio: true,
    ueber_mich: true,
    partner: true,
    kontakt: true,
  },
  texts: {
    claim_1: "Deine Idee.",
    claim_2: "Unsere Umsetzung.",
    claim_3: "Dein Ergebnis.",
    about_p1:
      "Ich heiße Justin, bin 21 Jahre alt und baue mir gerade — ohne großes Startkapital — Schritt für Schritt etwas Eigenes auf: ein Studio für Webdesign und Clothing.",
    about_p2:
      "Meine Idee ist einfach: Aus deiner Idee mache ich ein echtes Produkt. Websites und Online-Shops, die ich komplett selbst baue. Clothing-Designs, die ich von Hand skizziere. Und wer seine eigene Brand will, begleite ich bis sie verkaufsbereit ist.",
    about_quote:
      "„Ich bin keine Agentur mit zehn Ansprechpartnern — ich bin jung, motiviert und mache jedes Projekt persönlich. Genau das macht den Unterschied.“",
    pkg_plus_tagline: WEBSITE_PACKAGES.find((p) => p.id === "plus").tagline,
    pkg_premium_tagline: WEBSITE_PACKAGES.find((p) => p.id === "premium").tagline,
    pkg_plus_desc: WEBSITE_PACKAGES.find((p) => p.id === "plus").desc,
    pkg_premium_desc: WEBSITE_PACKAGES.find((p) => p.id === "premium").desc,
  },
  portfolio: null, // null = Komponente nutzt ihre eingebauten Einträge
};

const deepMerge = (base, override) => {
  if (!override || typeof override !== "object" || Array.isArray(override)) return override ?? base;
  const out = { ...base };
  for (const [k, v] of Object.entries(override)) {
    out[k] = v && typeof v === "object" && !Array.isArray(v) && base && typeof base[k] === "object" && !Array.isArray(base[k])
      ? deepMerge(base[k], v)
      : v;
  }
  return out;
};

const hexToRgbTriplet = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const SiteContentContext = createContext({
  content: DEFAULT_CONTENT,
  loaded: false,
  preview: false,
  version: 0,
});

export function SiteContentProvider({ children }) {
  const [state, setState] = useState({
    content: DEFAULT_CONTENT,
    loaded: false,
    preview: false,
    version: 0,
  });

  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).get("preview") === "1";
    const url = preview ? "/admin/ai/draft" : "/content/public";
    api
      .get(url)
      .then(({ data }) =>
        setState({
          content: deepMerge(DEFAULT_CONTENT, data.content || {}),
          loaded: true,
          preview: preview && data.has_draft === true,
          version: data.base_version || data.version || 0,
        })
      )
      .catch(() => setState((s) => ({ ...s, loaded: true })));
  }, []);

  // Akzentfarben live als CSS-Variablen setzen (Tailwind nutzt rgb(var(...) / alpha))
  useEffect(() => {
    const c = state.content.colors || {};
    const light = hexToRgbTriplet(c.accent);
    const dark = hexToRgbTriplet(c.accent_dark);
    if (light) document.documentElement.style.setProperty("--tw-goldlight", light);
    if (dark) document.documentElement.style.setProperty("--tw-gold", dark);
  }, [state.content]);

  return <SiteContentContext.Provider value={state}>{children}</SiteContentContext.Provider>;
}

export const useSiteContent = () => useContext(SiteContentContext);
